import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box } from '@/components/ui/Box';
import { Button } from '@/components/ui/button';
import { TeamsApi } from 'api-client/apis';
import { getApiConfiguration } from '@/api/utils';
import { AuthContext } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useTeams } from '@/contexts/TeamsContext';
import type { Invitation } from 'api-client/models';

const InvitationLanding = () => {
    const { invitationId } = useParams();
    const { currentTeam } = useTeams();
    const navigate = useNavigate();
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [invitation, setInvitation] = useState<Invitation | null>(null);

    const client = new TeamsApi(getApiConfiguration(token));

    const fetchInvitation = async () => {
        if (!invitationId) return;

        try {
            setLoading(true);
            setError(null);
            const response = await client.invitationDetailsRetrieve({ 
                invitationId: invitationId,
            });
            setInvitation(response);
        } catch (err) {
            console.log('Error fetching invitation:', err);
            setError('Failed to fetch invitation details.');
            toast.error('Failed to load invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptInvitation = async () => {
        if (!invitationId || !invitation) return;
    
        try {
            setLoading(true);
            setError(null);
            // await client.invitationsUpdate({ 
            //     id: invitationId, 
            //     invitation: {...invitation, isAccepted: true}, 
            //     teamSlug: currentTeam.slug 
            // });
            toast.success('Invitation accepted successfully');
            navigate('/invitation/signup/' + invitationId);
        } catch (err) {
            console.error('Error accepting invitation:', err);
            setError('Failed to accept invitation. Please try again.');
            toast.error('Failed to accept invitation');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvitation();
    }, [currentTeam?.slug, invitationId]);

    if (loading && !invitation) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#080B14]">
                <Box className="max-w-md w-full text-center">
                    <p className="text-gray-400">Loading invitation details...</p>
                </Box>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#080B14]">
            <Box className="max-w-md w-full text-center">
                <div className="flex items-center justify-center mb-8">
                    <img src="/logo icon white.svg" alt="Flawtrack Logo" className="h-8 w-8" />
                    <h1 className="text-2xl font-bold text-white ml-3">Flawtrack</h1>
                </div>
                <h3 className="text-lg font-bold text-white mb-4">Team Invitation</h3>
                {error ? (
                    <p className="text-red-500 mb-4">{error}</p>
                ) : invitation ? (
                    <div className="mb-6">
                        <p className="text-gray-400">
                            You've been invited to join {invitation.teamName}.
                        </p>
                    </div>
                ) : (
                    <p className="text-gray-400 mb-6">
                        Unable to load invitation details.
                    </p>
                )}
                <Button
                    onClick={handleAcceptInvitation}
                    disabled={loading || !invitation}
                    className="w-full"
                >
                    {loading ? 'Accepting...' : 'Accept Invitation'}
                </Button>
            </Box>
        </div>
    );
};

export default InvitationLanding;
